import React from 'react';
import { X, Clock, User, Tag, } from 'lucide-react';

interface TicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: {
    id: string;
    subject: string;
    status: string;
    priority: string;
    resolvedBy: string;
    service: string;
    createdAt: string;
    resolvedAt?: string;
    aiResponse: string;
    customerQuery: string;
    sentiment: string;
    category: string;
    resolution: string;
  };
}

const TicketModal: React.FC<TicketModalProps> = ({ isOpen, onClose, ticket }) => {
  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl bg-[#0F1824] rounded-2xl p-8 z-50 border border-white/[0.05] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Ticket #{ticket.id}</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.05]">
            <div className="flex items-center gap-2 text-gray-400 mb-2">
              <Tag className="h-4 w-4" />
              <span className="text-sm">Status</span>
            </div>
            <span className={`inline-flex px-2 py-1 rounded-full text-sm ${
              ticket.status === 'Resolved' ? 'bg-green-500/10 text-green-500' :
              ticket.status === 'In Progress' ? 'bg-yellow-500/10 text-yellow-500' :
              'bg-blue-500/10 text-blue-500'
            }`}>
              {ticket.status}
            </span>
          </div>

          <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.05]">
            <div className="flex items-center gap-2 text-gray-400 mb-2">
              <User className="h-4 w-4" />
              <span className="text-sm">Resolved By</span>
            </div>
            <span className="text-white">{ticket.resolvedBy}</span>
          </div>

          <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.05]">
            <div className="flex items-center gap-2 text-gray-400 mb-2">
              <Clock className="h-4 w-4" />
              <span className="text-sm">Resolution Time</span>
            </div>
            <span className="text-white">{ticket.resolvedAt || 'Pending'}</span>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white/[0.03] rounded-lg p-6 border border-white/[0.05]">
            <h3 className="text-lg font-semibold text-white mb-4">Customer Query</h3>
            <p className="text-gray-300">{ticket.customerQuery}</p>
          </div>

          <div className="bg-white/[0.03] rounded-lg p-6 border border-white/[0.05]">
            <h3 className="text-lg font-semibold text-white mb-4">AI Analysis</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <span className="text-gray-400 text-sm">Sentiment</span>
                <p className="text-white mt-1">{ticket.sentiment}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Category</span>
                <p className="text-white mt-1">{ticket.category}</p>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-gray-400 text-sm">AI Response</span>
              <p className="text-white mt-1">{ticket.aiResponse}</p>
            </div>
          </div>

          <div className="bg-white/[0.03] rounded-lg p-6 border border-white/[0.05]">
            <h3 className="text-lg font-semibold text-white mb-4">Resolution</h3>
            <p className="text-gray-300">{ticket.resolution}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketModal;